import { supabase, N8N_BASE_URL } from '@/lib/supabase';

export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getCredits(): Promise<number | null> {
  const { data, error } = await supabase.from('credits').select('balance').maybeSingle();
  if (error) return null;
  return data ? (data.balance as number) : null;
}

// 依「动作」扣点(成本由后端 credit_costs 决定,client 不能伪造)。余额不足会 throw。
// 带上 character_id/task_id,让会员「我的纪录」能点进去看内容与影片。
export async function spendCredits(action: string, characterId?: number | null, taskId?: number | null): Promise<number> {
  const { data, error } = await supabase.rpc('spend_credits', { p_action: action, p_character_id: characterId ?? null, p_task_id: taskId ?? null });
  if (error) {
    if ((error.message || '').includes('insufficient')) throw new Error('点数不足,请联络管理员加值');
    if ((error.message || '').includes('not authenticated')) throw new Error('请先登入');
    throw new Error(error.message || '扣点失败');
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('credits-changed'));
  return data as number;
}

// 按「单位数」动态扣点(影片等按时长计费用):total = credit_costs[action] × max(1,units)。单价仍由后端决定。
export async function spendCreditsUnits(action: string, units: number, characterId?: number | null, taskId?: number | null): Promise<number> {
  const { data, error } = await supabase.rpc('spend_credits_units', { p_action: action, p_units: Math.max(1, Math.round(units || 1)), p_character_id: characterId ?? null, p_task_id: taskId ?? null });
  if (error) {
    if ((error.message || '').includes('insufficient')) throw new Error('点数不足,请联络管理员加值');
    if ((error.message || '').includes('not authenticated')) throw new Error('请先登入');
    throw new Error(error.message || '扣点失败');
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('credits-changed'));
  return data as number;
}

export interface WardrobeSpendResult { balance: number; charged: boolean; free_remaining: number }

/** 模特换衣：每张角色形象图免费预览 2 次，之后才扣 wardrobe_lockface 点数 */
export async function spendWardrobeLockface(characterId: number): Promise<WardrobeSpendResult> {
  const { data, error } = await supabase.rpc('spend_wardrobe_lockface', { p_character_id: characterId });
  if (error) {
    if ((error.message || '').includes('insufficient')) throw new Error('点数不足,请联络管理员加值');
    if ((error.message || '').includes('no avatar')) throw new Error('这个角色还没有形象图,无法换装');
    if ((error.message || '').includes('not authenticated')) throw new Error('请先登入');
    throw new Error(error.message || '扣点失败');
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('credits-changed'));
  const row = (data ?? {}) as Record<string, unknown>;
  return {
    balance: Number(row.balance) || 0,
    charged: Boolean(row.charged),
    free_remaining: Number(row.free_remaining) || 0,
  };
}

export async function getWardrobeFreeRemaining(characterId: number): Promise<number> {
  const { data, error } = await supabase.rpc('get_wardrobe_free_remaining', { p_character_id: characterId });
  if (error) return 0;
  return Number(data) || 0;
}

export interface CreditCost { action: string; cost: number; label: string | null }

export async function getCreditCosts(): Promise<CreditCost[]> {
  const { data, error } = await supabase.from('credit_costs').select('*').order('action');
  if (error) return [];
  return (data ?? []) as CreditCost[];
}

export interface MyActivity { id: number; created_at: string; reason: string | null; amount: number; character_id: number | null; task_id: number | null }

// 会员自己的操作 / 生成历史(RLS 只回自己的)
export async function getMyActivity(limit = 100): Promise<MyActivity[]> {
  const { data, error } = await supabase
    .from('credit_logs')
    .select('id,created_at,reason,amount,character_id,task_id')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return (data ?? []) as MyActivity[];
}

// 管理员重设会员密码(走 n8n,service_role 不外露)
export async function adminResetPassword(userId: string, newPassword: string): Promise<void> {
  const { data: s } = await supabase.auth.getSession();
  const token = s.session?.access_token;
  if (!token) throw new Error('未登入');
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_admin_reset_password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, user_id: userId, password: newPassword }),
  });
  const j = await res.json().catch(() => ({}));
  if (!j.ok) throw new Error(j.message || '重设失败');
}

// 管理员新增会员(登入制、帐号由管理员开立;走 n8n + service_role)
export async function adminCreateMember(email: string, password: string, credits: number): Promise<void> {
  const { data: s } = await supabase.auth.getSession();
  const token = s.session?.access_token;
  if (!token) throw new Error('未登入');
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_admin_create_member`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, email, password, credits }),
  });
  const j = await res.json().catch(() => ({}));
  if (!j.ok) throw new Error(j.message || '新增会员失败');
}

export async function isAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_admin');
  if (error) return false;
  return Boolean(data);
}

// 目前登入者的 user id(用于判断角色是否属于自己)
export async function getUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

// 登入时记录(IP/地理位置由 n8n 端取得)
export async function logLogin(email: string, userId: string) {
  if (!N8N_BASE_URL) return;
  try {
    await fetch(`${N8N_BASE_URL}/webhook/astraea_log_login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, user_id: userId }),
    });
  } catch { /* 记录失败不影响登入 */ }
}

// ===== 管理员 API =====
export interface AdminMember { user_id: string; email: string; balance: number; created_at: string; last_sign_in_at: string | null }
export interface AdminActivity { created_at: string; email: string | null; amount: number; reason: string | null }
export interface AdminLogin { created_at: string; email: string | null; ip: string | null; location: string | null; user_agent: string | null }

export async function adminListMembers(): Promise<AdminMember[]> {
  const { data, error } = await supabase.rpc('admin_list_members');
  if (error) throw new Error(error.message);
  return (data ?? []) as AdminMember[];
}

export async function adminSetCredits(userId: string, balance: number): Promise<number> {
  const { data, error } = await supabase.rpc('admin_set_credits', { p_user: userId, p_balance: balance });
  if (error) throw new Error(error.message);
  return data as number;
}

export async function adminAdjustCredits(userId: string, delta: number, reason: string): Promise<number> {
  const { data, error } = await supabase.rpc('admin_adjust_credits', { p_user: userId, p_delta: delta, p_reason: reason });
  if (error) throw new Error(error.message);
  return data as number;
}

export async function adminSetCost(action: string, cost: number): Promise<void> {
  const { error } = await supabase.rpc('admin_set_cost', { p_action: action, p_cost: cost });
  if (error) throw new Error(error.message);
}

export async function adminRecentActivity(limit = 100): Promise<AdminActivity[]> {
  const { data, error } = await supabase.rpc('admin_recent_activity', { p_limit: limit });
  if (error) throw new Error(error.message);
  return (data ?? []) as AdminActivity[];
}

export async function adminRecentLogins(limit = 100): Promise<AdminLogin[]> {
  const { data, error } = await supabase.rpc('admin_recent_logins', { p_limit: limit });
  if (error) throw new Error(error.message);
  return (data ?? []) as AdminLogin[];
}

// ===== 用量 / 成本 =====
export interface UsageRow { action: string; label: string; cnt: number; points: number; money_cost: number; est_money: number }

export async function adminTotalCredits(): Promise<number> {
  const { data, error } = await supabase.rpc('admin_total_credits');
  if (error) throw new Error(error.message);
  return Number(data) || 0;
}

export async function adminUsageSummary(days = 30): Promise<UsageRow[]> {
  const { data, error } = await supabase.rpc('admin_usage_summary', { p_days: days });
  if (error) throw new Error(error.message);
  return (data ?? []) as UsageRow[];
}

export async function adminSetMoneyCost(action: string, money: number): Promise<void> {
  const { error } = await supabase.rpc('admin_set_money_cost', { p_action: action, p_money: money });
  if (error) throw new Error(error.message);
}

// ===== 兜底形象库 =====
export interface FallbackAvatar { id: number; gender: string; style: string | null; age_range: string | null; scene: string | null; label: string | null; url: string; claimed_by?: string | null }

// 管理员:全部(含已认领,用于后台管理)
export async function getFallbackAvatars(): Promise<FallbackAvatar[]> {
  const { data, error } = await supabase.from('fallback_avatars').select('*').order('id', { ascending: false });
  if (error) return [];
  return (data ?? []) as FallbackAvatar[];
}

// 会员:只看「未被认领」的可选池
export async function getAvailableFallbacks(gender?: string): Promise<FallbackAvatar[]> {
  let q = supabase.from('fallback_avatars').select('*').is('claimed_by', null).order('id', { ascending: false });
  if (gender) q = q.eq('gender', gender);
  const { data, error } = await q;
  if (error) return [];
  return (data ?? []) as FallbackAvatar[];
}

// 会员认领一张兜底形象(独家,原子;成功后该图从池中消失并设为角色头像)
export async function claimFallbackAvatar(id: number, characterId: number): Promise<string> {
  const { data, error } = await supabase.rpc('claim_fallback_avatar', { p_id: id, p_character_id: characterId });
  if (error) {
    if ((error.message || '').includes('already claimed')) throw new Error('这张已被别人选走了,换一张吧');
    throw new Error(error.message || '认领失败');
  }
  return data as string;
}

export async function addFallbackAvatar(file: File, meta: { gender: string; style?: string; age_range?: string; scene?: string; label?: string }): Promise<void> {
  const { data: s } = await supabase.auth.getSession();
  const token = s.session?.access_token;
  if (!token) throw new Error('未登入');
  if (!N8N_BASE_URL) throw new Error('系统尚未设定,请联络管理员');
  const base64: string = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const res = await fetch(`${N8N_BASE_URL}/webhook/astraea_fallback_avatar_add`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, base64, ext, ...meta }),
  });
  const j = await res.json().catch(() => ({}));
  if (!j.ok) throw new Error(j.message || '上传失败');
}

export async function deleteFallbackAvatar(id: number): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_fallback', { p_id: id });
  if (error) throw new Error(error.message);
}
